import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import ExerciseEvaluation from "features/Evaluation/ExerciseEvaluation";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { clearSessionId, getResults, setNewEnvelopes } from "store/slices/exercise";
import { getUserEmail } from "store/slices/user";

import { gtmExerciseSelfEvaluation } from "utils/gtmEvents";
import { useCalendarRange } from "utils/hooks/useCalendarRange";
import { processApiError } from "utils/processApiError";
import { removeSessionFromStorage } from "utils/sessionStorageHandler";

import type { AssignmentCloseInput } from "api/generated";
import { calendarTimetableTag } from "api/calendarTimetableTag";
import { api, useLazyGetAssignmentsEnvelopesQuery, useSessionCloseMutation } from "api/generated";

interface Props {
  onSubmit: () => void;
}

// AIDEV-NOTE: Bez limitu visel `sessionClose` na mobilu skoro dvě minuty — server ho zpracoval,
// odpověď se ztratila a tlačítko nereagovalo. 30 s pokryje i studený start backendu (7–18 s).
// Opakování po dokončeném uzavření vrátí prázdný výsledek. Souběh s ještě běžícím prvním
// uzavřením by trénink zpracoval dvakrát (backend nemá zámek) — v prod za 14 dní max 10,6 s
// z 48 797 uzavření, takže hrozí jen při zaseknutém workeru. [TD-464]
const CLOSE_TIMEOUT_MS = 30_000;

// Výpadek přenosu (`Failed to fetch`, `Load failed`), zrušení po limitu nebo 5xx od proxy —
// tady má smysl zkusit to znovu. GraphQL chyba v odpovědi se opakováním nespraví.
const isRetryableCloseError = (error: unknown) => {
  const { name, message } = (error ?? {}) as { name?: unknown; message?: unknown };

  return (
    name === "TypeError" ||
    name === "AbortError" ||
    (typeof message === "string" && /^GraphQL Error \(Code: 5\d\d\)/.test(message))
  );
};

const calculateAbilityScore = (values: number | Array<number | undefined> | undefined) => {
  if (values === undefined) return 0;
  if (typeof values === "number") return values;

  const filled = values.filter((value): value is number => typeof values === "number");
  if (filled.length === 0) return 0;

  const sum = filled.reduce((accumulator, value) => accumulator + value, 0);

  return sum / filled.length;
};

const Summary: FC<Props> = ({ onSubmit }) => {
  const dispatch = useAppDispatch();
  const [closeSession, { isLoading: isClosing, error: apiMutationError }] = useSessionCloseMutation();
  const [getNewEnvelopes, { error: lazyApiError }] = useLazyGetAssignmentsEnvelopesQuery();

  const results = useAppSelector(getResults);
  const userEmail = useAppSelector(getUserEmail);
  const { dateFrom, dateTo, isUserLoaded } = useCalendarRange();

  const [mood, setMood] = useState<number>(0);
  const [moodText, setMoodText] = useState("");
  const [selfReflectionText, setSelfReflectionText] = useState("");
  const [hasCloseFailed, setHasCloseFailed] = useState(false);
  const loadingData = useRef(false);

  const getAssignmentsEnvelopes = async (ids: string[], assignmentEnvelopes: Record<string, string>) => {
    try {
      const response = await getNewEnvelopes({ assignments: ids });

      if ("data" in response) {
        const assignments = response.data?.userAssignments.items;

        if (assignments === undefined) return;

        for (const assignment of assignments) {
          if (typeof assignment.id !== "string" || typeof assignment.envelope !== "string") continue;

          assignmentEnvelopes[assignment.id] = assignment.envelope;
        }
      }
    } catch (error) {
      processApiError(error);
    }
  };

  const chunkArray = (array: string[]) => {
    const maxItemsInRequest = 30;

    const chunks = [];
    for (let i = 0; i < array.length; i += maxItemsInRequest) {
      chunks.push(array.slice(i, i + maxItemsInRequest));
    }
    return chunks;
  };

  const getEnvelopes = async () => {
    try {
      const assignmentEnvelopes: Record<string, string> = {};
      const chunks = chunkArray(Object.keys(results));

      const assignmentPromises = chunks.map(async ids => {
        await getAssignmentsEnvelopes(ids, assignmentEnvelopes).catch(console.error);
      });

      await Promise.all(assignmentPromises);

      dispatch(setNewEnvelopes(assignmentEnvelopes));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getEnvelopes().catch(console.error);
  }, [results]);

  useEffect(() => {
    if (lazyApiError === undefined) return;

    processApiError(lazyApiError);
  }, [lazyApiError]);

  useEffect(() => {
    if (apiMutationError === undefined) return;

    processApiError(apiMutationError);
  }, [apiMutationError]);

  const handleSubmitClick = () => {
    if (loadingData.current) return;
    loadingData.current = true;

    dispatch(clearSessionId());
    const additionalValues: AssignmentCloseInput[] = [];

    for (const [userAssignmentId, resultData] of Object.entries(results)) {
      additionalValues.push({
        userAssignmentId,
        timeLearnReal: resultData.timeLearn ?? 0,
        abilityScore: calculateAbilityScore(resultData.abilityScore),
        tip: resultData.tips.some(some => some),
      });
    }

    if (!hasCloseFailed) gtmExerciseSelfEvaluation(mood);
    setHasCloseFailed(false);

    const request = closeSession({ mood: mood ?? 0, moodText, selfReflectionText, assignmentsClose: additionalValues });
    const timeout = setTimeout(() => {
      request.abort();
    }, CLOSE_TIMEOUT_MS);

    request
      .then(result => {
        if ("error" in result && isRetryableCloseError(result.error)) {
          setHasCloseFailed(true);
          return;
        }

        // Uzavření zvýšilo počítadla na serveru (sessions_exercised, days_fulfills_plan). Uživatele
        // bez chyby obnovíme na pozadí, AuthenticatedArea z něj naplní lištu. Při GraphQL chybě
        // (trénink se neuložil, student jde dál jako dosud) se nic nemění. Přetypování `never`:
        // typ generovaného `api` tagy nezná, přidává je až applyCachePolicy za běhu. [TD-472]
        //
        // AIDEV-NOTE: Stejně se invaliduje záznam kalendáře (`getTimetable` s rozsahem z `getCalendarRange`,
        // tedy ze stejných hodnot jako stránka kalendáře). Prefetch při najetí na odkaz během tréninku by
        // jinak nechal v cache dnešek bez jahody až 15 minut. Týdenní dotaz `ExerciseSessionArea` má jiný
        // rozsah, takže se nenačte znovu (přehled výsledků by se vyměnil za „Dneska už jsi úkoly zvládl“).
        // Bez načteného studenta se rozsah nezná a záznam kalendáře nemohl vzniknout. [TD-476]
        if (!("error" in result)) {
          const tags = isUserLoaded ? ["CurrentUser", calendarTimetableTag({ dateFrom, dateTo })] : ["CurrentUser"];
          dispatch(api.util.invalidateTags(tags as never));
        }

        onSubmit();
        removeSessionFromStorage(userEmail);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        clearTimeout(timeout);
        loadingData.current = false;
      });
  };

  return (
    <ExerciseEvaluation
      mood={mood}
      moodText={moodText}
      selfReflectionText={selfReflectionText}
      results={results}
      onMoodSelect={setMood}
      onMoodTextChange={setMoodText}
      onSelfReflectionTextChange={setSelfReflectionText}
      onSubmit={handleSubmitClick}
      isSubmitting={isClosing}
      hasSubmitFailed={hasCloseFailed}
    />
  );
};

export default Summary;
